import React, { useState } from "react";
import {
  Box,
  TextField,
  Switch,
  Typography,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { Settings, State } from "./state";
import { isAuthorizedSharedState } from "../api";

export type JSONValue =
  | string
  | number
  | boolean
  | JSONObject
  | JSONArray
  | undefined;
export interface JSONObject {
  [key: string]: JSONValue;
}
interface JSONArray extends Array<JSONValue> {}

interface JsonEditorProps {
  data: JSONObject;
  onChange?: (value: JSONObject) => void;
  setState: React.Dispatch<React.SetStateAction<State>>;
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  data,
  onChange,
  setState,
}) => {
  const [json, setJson] = useState<JSONObject>(data);

  const handleChange = (key: string, value: JSONValue) => {
    const updated = { ...json, [key]: value };
    setJson(updated);
    onChange?.(updated);
  };

  const submit = async () => {
    if (
      !(json.meals as unknown as string[]).includes(
        json.defaultMeal as unknown as string,
      )
    ) {
      alert("the default meal must be in the meals");
      return;
    }
    if (
      !Object.keys(json.isleToIngredients as unknown as object) // TODO - dumb typescript
        .map((k) => !isNaN(Number(k)))
        .every((x) => {
          return x;
        })
    ) {
      alert("All isles must be a number");
      return;
    }
    if (
      !(await isAuthorizedSharedState({
        sharedStateName: json.sharedStateName,
      }))
    ) {
      alert("Not authorized for shared state");
      return;
    }
    setState((prevState: State) => {
      return {
        ...prevState,
        settings: json as unknown as Settings,
      };
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Settings
      </Typography>

      <Stack spacing={2}>
        {Object.entries(json).map(([key, value]) => (
          <JsonField
            key={key}
            name={key}
            value={value}
            onChange={(v) => handleChange(key, v)}
          />
        ))}
      </Stack>
      <Button
        style={{ marginTop: "3%" }}
        onClick={() => {
          submit();
        }}
        variant="outlined"
      >
        Submit
      </Button>
    </Paper>
  );
};

export function camelCaseToSpaces(i: string) {
  return i.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

// Recursive field renderer
const JsonField: React.FC<{
  name: string;
  value: JSONValue;
  onChange: (value: JSONValue) => void;
}> = ({ name, value, onChange }) => {
  const splitName = camelCaseToSpaces(name);
  const [newKey, setNewKey] = useState("");
  const renderField = () => {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "undefined"
    ) {
      return (
        <TextField
          fullWidth
          size="small"
          label={splitName}
          value={value}
          onChange={(e) =>
            onChange(
              typeof value === "number"
                ? Number(e.target.value)
                : e.target.value,
            )
          }
        />
      );
    }

    if (typeof value === "boolean") {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>{splitName}</Typography>
          <Switch
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
        </Stack>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {splitName}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              fullWidth
              size="small"
              multiline
              value={value.join("\n")}
              onChange={(e) => {
                const lines = e.target.value.split("\n");
                // .map((line) => line.trim());
                onChange(lines);
              }}
            />
          </Stack>
        </Box>
      );
    }

    if (typeof value === "object" && value !== null) {
      return (
        <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 2, mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {splitName}
          </Typography>
          {Object.entries(value).map(([childKey, childVal]) => (
            <JsonField
              key={childKey}
              name={childKey}
              value={childVal}
              onChange={(v) => onChange({ ...value, [childKey]: v })}
            />
          ))}
          <TextField
            size="small"
            label="New key"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
          />
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              // split then join to camel case
              const newKeyArray = newKey.trim().split(" ");
              const k = [
                newKeyArray[0],
                ...newKeyArray.slice(1).map((i) => {
                  return i.charAt(0).toUpperCase() + i.slice(1);
                }),
              ].join("");
              if (!k) return;
              // if (value && !isNaN(Number(k))) return // TODO - check if isle are numbers early
              if (value.hasOwnProperty(k)) {
                alert("That key already exists.");
                return;
              }
              onChange({
                ...value,
                [k]: [],
              });
              setNewKey("");
            }}
          >
            Add Key
          </Button>
        </Box>
      );
    }

    return null;
  };

  return <>{renderField()}</>;
};

export default JsonEditor;
